import { X } from 'lucide-react'
import type { ReactNode } from 'react'
import { Button } from '@/components/ui/button'

/** The most records one bulk action takes through "Alle n selecteren" (the server's limit). */
export const MAX_QUERY_SELECTION = 5000

const LIMIT_TEXT = 'Maximaal 5.000 tegelijk'

/**
 * The bar that takes the place of the filter bar while rows are ticked (design §4.2, §5.6): how
 * many, "Alle n selecteren" once the whole page is ticked and there is more, the menus (children)
 * and a way to let go. Bulk is for the desktop table; the bar is hidden below 768 px.
 */
export function BulkActionBar({
  count,
  total,
  pageCount,
  allMatching,
  noun,
  onSelectAll,
  onClear,
  children,
}: {
  /** How many are selected. */
  count: number
  /** How many match the filter. */
  total: number
  /** How many rows the page has. */
  pageCount: number
  /** Everything that matches is selected, not just what the page shows. */
  allMatching: boolean
  /** "9 contacten", "1 contact". */
  noun: (count: number) => string
  onSelectAll: () => void
  onClear: () => void
  children: ReactNode
}) {
  const offerAll = count === pageCount && total > pageCount && !allMatching
  const tooMany = total > MAX_QUERY_SELECTION
  return (
    <div role="region" aria-label="Bulkacties" className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted px-3 py-2 max-md:hidden">
      <span aria-live="polite" className="text-sm font-medium">
        {allMatching ? `Alle ${noun(total)} geselecteerd` : `${count} geselecteerd`}
      </span>
      {offerAll ? (
        // A disabled button takes no pointer events, so a mouse never sees its title: the wrapper
        // carries the reason for the hover, the button's own title is its description for a screen reader.
        <span title={tooMany ? LIMIT_TEXT : undefined} className="inline-flex">
          <Button type="button" variant="link" size="sm" disabled={tooMany} title={tooMany ? LIMIT_TEXT : undefined} onClick={onSelectAll}>
            {`Alle ${total} selecteren`}
          </Button>
        </span>
      ) : null}
      {children}
      <Button type="button" variant="ghost" size="icon-sm" className="ml-auto" aria-label="Selectie wissen" onClick={onClear}>
        <X />
      </Button>
    </div>
  )
}
